/**
 * Add CVO View
 * Manages CVO users - view list and add new CVOs
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';


import { ConfirmDialog, BaseDialog, Button, FormField, Input, MultiSelect } from '../../components/dialogs';
import cvoService from '../../services/cvoService';
import {
  selectCVOPrivileges,
} from '../../store/slices/settingsSlice';
import { exportToPDF, exportToExcel, exportToCSV } from '../../utils/tableExport';
import { obfuscateEmail } from '../../utils/emailObfuscation';
import TablePagination from '../../components/TablePagination';

const AddCVOView = () => {

  // Redux state selectors
  const cvoPrivilegesRaw = useSelector(selectCVOPrivileges);

  // Local state
  const [cvoList, setCvoList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const downloadDropdownRef = useRef(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [cvoToDelete, setCvoToDelete] = useState(null);
  const [editCVO, setEditCVO] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [newCVO, setNewCVO] = useState({
    name: '',
    email: '',
    mobile: '',
    menu_ids: [],
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filter only active items
  const filterActive = (items) => {
    if (!Array.isArray(items)) return [];
    return items.filter(item => {
      if (typeof item === 'string') return true;
      return item &&
        (!item.status || item.status === 'Active') &&
        !item.deleted_at;
    });
  };

  const cvoPrivileges = filterActive(cvoPrivilegesRaw);

  // Fetch CVOs on component mount
  useEffect(() => {
    fetchCVOs();
  }, []);

  const fetchCVOs = async () => {
    setLoading(true);
    try {
      const response = await cvoService.getCVOs();
      if (response.success) {
        const data = response.data?.data || response.data || [];
        setCvoList(Array.isArray(data) ? data : []);
      } else {
        showNotification(response.message || 'Failed to fetch CVOs', 'error');
      }
    } catch (error) {
      showNotification('Error fetching CVOs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  const handleFieldChange = (field, value) => {
    setNewCVO({ ...newCVO, [field]: value });
  };

  const handleAddCVO = async () => {
    // Validation
    if (!newCVO.name || !newCVO.email || !newCVO.mobile || !newCVO.menu_ids || newCVO.menu_ids.length === 0) {
      showNotification('Please fill all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: newCVO.name,
        email: newCVO.email,
        mobile: newCVO.mobile,
        menu_ids: newCVO.menu_ids,
      };

      const response = await cvoService.createCVO(payload);
      if (response.success) {
        showNotification('CVO added successfully', 'success');
        setShowAddDialog(false);
        setNewCVO({
          name: '',
          email: '',
          mobile: '',
          menu_ids: [],
        });
        fetchCVOs();
      } else {
        showNotification(response.message || 'Failed to add CVO', 'error');
      }
    } catch (error) {
      showNotification('Error adding CVO', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (cvo) => {
    const menuIds = cvo.assigned_menus?.map(menu => menu.id) ||
      cvo.menu_ids ||
      cvo.permission_access_id ||
      [];

    setEditCVO({
      id: cvo.id,
      name: cvo.name || '',
      email: cvo.email || '',
      mobile: cvo.mobile || cvo.phone || '',
      menu_ids: menuIds,
    });
    setShowEditDialog(true);
  };

  const handleUpdateCVO = async () => {
    // Validation
    if (!editCVO.name || !editCVO.email || !editCVO.mobile || !editCVO.menu_ids || editCVO.menu_ids.length === 0) {
      showNotification('Please fill all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: editCVO.name,
        email: editCVO.email,
        mobile: editCVO.mobile,
        menu_ids: editCVO.menu_ids,
      };

      const response = await cvoService.updateCVO(editCVO.id, payload);
      if (response.success) {
        showNotification('CVO updated successfully', 'success');
        setShowEditDialog(false);
        setEditCVO(null);
        fetchCVOs();
      } else {
        showNotification(response.message || 'Failed to update CVO', 'error');
      }
    } catch (error) {
      showNotification('Error updating CVO', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (cvo) => {
    setCvoToDelete(cvo);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!cvoToDelete) return;

    setLoading(true);
    try {
      const response = await cvoService.deleteCVO(cvoToDelete.id);
      if (response.success) {
        showNotification('CVO deleted successfully', 'success');
        setShowDeleteConfirm(false);
        setCvoToDelete(null);
        fetchCVOs();
      } else {
        showNotification(response.message || 'Failed to delete CVO', 'error');
      }
    } catch (error) {
      showNotification('Error deleting CVO', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getPrivilegeName = (privilegeId) => {
    const privilege = cvoPrivileges.find(p =>
      (typeof p === 'object' ? p.id : p) === privilegeId
    );
    return privilege ? (typeof privilege === 'object' ? privilege.name : privilege) : privilegeId;
  };

  const getPrivilegesList = (cvo) => {
    // Use assigned_menus from API response (these are the privileges)
    if (cvo.assigned_menus && Array.isArray(cvo.assigned_menus) && cvo.assigned_menus.length > 0) {
      return cvo.assigned_menus;
    }

    // Fallback to menu_ids or privileges if assigned_menus is not available
    const privilegeIds = cvo.menu_ids || cvo.privileges || cvo.permission_access_id;
    if (!privilegeIds || (Array.isArray(privilegeIds) && privilegeIds.length === 0)) {
      return [];
    }

    const ids = Array.isArray(privilegeIds) ? privilegeIds : [privilegeIds];
    return ids.map(id => {
      const privilege = cvoPrivileges.find(p =>
        (typeof p === 'object' ? p.id : p) === id
      );
      return {
        id: id,
        name: privilege ? (typeof privilege === 'object' ? privilege.name : privilege) : `Menu ${id}`
      };
    });
  };

  // Transform CVO data for DataGrid
  const transformCVOData = (cvo) => {
    return {
      id: cvo.id,
      name: cvo.name || '',
      email: cvo.email || '',
      mobile: cvo.mobile || cvo.phone || '',
      status: cvo.status || 'N/A',
      permissions: getPrivilegesList(cvo),
      _original: cvo
    };
  };

  const transformedCVOs = Array.isArray(cvoList)
    ? cvoList.map(transformCVOData)
    : [];

  // Filter CVOs based on search term
  const filteredCVOs = transformedCVOs.filter(cvo => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const name = (cvo.name || '').toString().toLowerCase();
    const email = (cvo.email || '').toString().toLowerCase();
    const mobile = (cvo.mobile || '').toString().toLowerCase();
    const status = (cvo.status || '').toString().toLowerCase();
    const permissions = (cvo.permissions || []).map(p => (p.name || '').toString().toLowerCase()).join(' ');

    return name.includes(searchLower) ||
      email.includes(searchLower) ||
      mobile.includes(searchLower) ||
      status.includes(searchLower) ||
      permissions.includes(searchLower);
  });

  // Pagination Logic
  const totalItems = filteredCVOs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedCVOs = filteredCVOs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Close download dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get columns for export
  const getColumns = () => [
    { field: 'name', headerName: 'Name' },
    {
      field: 'email',
      headerName: 'Email',
      renderCell: (params) => obfuscateEmail(params.value || ''),
    },
    { field: 'mobile', headerName: 'Mobile' },
    { field: 'status', headerName: 'Status' },
  ];

  // Get filtered data for export
  const getFilteredData = () => {
    return filteredCVOs.map(cvo => ({
      name: cvo.name || '',
      email: cvo.email || '',
      mobile: cvo.mobile || '',
      status: cvo.status || '',
      permissions: (cvo.permissions || []).map(p => p.name || '').join('; '),
    }));
  };

  const handleExportCSV = () => {
    try {
      const filteredData = getFilteredData();
      exportToCSV(filteredData, getColumns(), 'cvo-users');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const handleExportExcel = () => {
    try {
      const filteredData = getFilteredData();
      exportToExcel(filteredData, getColumns(), 'cvo-users');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel. Please try again.');
    }
  };

  const handleExportPDF = () => {
    try {
      const filteredData = getFilteredData();
      exportToPDF(filteredData, getColumns(), 'cvo-users');
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };



  const addDialogFooter = (
    <>
      <Button variant="secondary" onClick={() => setShowAddDialog(false)} disabled={loading}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleAddCVO} disabled={loading}>
        {loading ? 'Adding...' : 'Add CVO'}
      </Button>
    </>
  );

  return (
    <>
      <div className="h-full flex flex-col min-h-0">
        {/* Header with Search (Left) and Buttons (Right) */}
        <div className="flex justify-between items-center pb-4 flex-shrink-0 gap-4">
          {/* Search Bar - Left Aligned */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 w-96">
            <div className="flex items-center space-x-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 outline-none text-gray-700"
                placeholder="Search by name, email, mobile, status, or permissions..."
              />
            </div>
          </div>

          {/* Buttons - Right Aligned */}
          <div className="flex items-center gap-3">
            {/* Download Button with Dropdown */}
            <div className="relative" ref={downloadDropdownRef}>
              <button
                onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Download</span>
                <svg className={`w-4 h-4 transition-transform ${showDownloadDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Download Dropdown Menu */}
              {showDownloadDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={handleExportCSV}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Download CSV</span>
                    </button>
                    <button
                      onClick={handleExportExcel}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Download Excel</span>
                    </button>
                    <button
                      onClick={handleExportPDF}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span>Download PDF</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Add CVO Button */}
            <button
              onClick={() => setShowAddDialog(true)}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium disabled:opacity-50"
              disabled={loading}
            >
              Add CVO
            </button>
          </div>
        </div>

        {/* CVOs Table */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 flex-1 flex flex-col overflow-hidden min-h-0">
          <div className="flex-1 overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 relative">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Email</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Mobile</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Privileges</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan="6" className="px-6 py-12 text-center text-gray-500">Loading...</td></tr>
                ) : filteredCVOs.length === 0 ? (
                  <tr><td colSpan="6" className="px-6 py-12 text-center text-gray-500">No CVOs found</td></tr>
                ) : (
                  paginatedCVOs.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{obfuscateEmail(row.email)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.mobile}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex flex-wrap gap-1">
                          {row.permissions && row.permissions.length > 0 ? (
                            row.permissions.map(menu => (
                              <span
                                key={menu.id}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                              >
                                {menu.name}
                              </span>
                            ))
                          ) : <span className="text-gray-400 text-sm">No permissions</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const status = row.status;
                          let styles = "bg-gray-100 text-gray-800 border-gray-200";
                          if (status === 'Active') styles = "bg-green-50 text-green-700 border-green-200";
                          else styles = "bg-yellow-50 text-yellow-800 border-yellow-200";

                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
                              {status}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleEditClick(row._original)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="Edit CVO"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteClick(row._original)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Delete CVO"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(newSize) => {
              setItemsPerPage(newSize);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {/* Add CVO Dialog */}
      <BaseDialog
        isOpen={showAddDialog}
        title="Add New CVO"
        onClose={() => setShowAddDialog(false)}
        footer={addDialogFooter}
        size="medium"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Name" required>
            <Input
              id="cvo-name"
              value={newCVO.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="Enter CVO name"
              disabled={loading}
            />
          </FormField>

          <FormField label="Email" required>
            <Input
              type="email"
              id="cvo-email"
              value={newCVO.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              placeholder="Enter email address"
              disabled={loading}
            />
          </FormField>

          <FormField label="Mobile Number" required>
            <Input
              type="tel"
              id="cvo-mobile"
              value={newCVO.mobile}
              onChange={(e) => handleFieldChange('mobile', e.target.value)}
              placeholder="Enter mobile number"
              disabled={loading}
              maxLength={10}
            />
          </FormField>

          <div className="md:col-span-2">
            <FormField label="Menu IDs" required hint="Select one or more menu IDs for this CVO">
              <MultiSelect
                id="cvo-menu-ids"
                options={cvoPrivileges}
                value={newCVO.menu_ids || []}
                onChange={(values) => handleFieldChange('menu_ids', values)}
                placeholder="Select menu IDs..."
                disabled={loading}
              />
            </FormField>
          </div>
        </div>
      </BaseDialog>

      {/* Edit CVO Dialog */}
      <BaseDialog
        isOpen={showEditDialog && !!editCVO}
        title="Edit CVO"
        onClose={() => {
          setShowEditDialog(false);
          setEditCVO(null);
        }}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowEditDialog(false);
                setEditCVO(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdateCVO} disabled={loading}>
              {loading ? 'Updating...' : 'Update CVO'}
            </Button>
          </>
        }
        size="medium"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Name" required>
            <Input
              id="edit-cvo-name"
              value={editCVO?.name || ''}
              onChange={(e) => setEditCVO({ ...editCVO, name: e.target.value })}
              placeholder="Enter CVO name"
              disabled={loading}
            />
          </FormField>

          <FormField label="Email" required>
            <Input
              type="email"
              id="edit-cvo-email"
              value={editCVO?.email || ''}
              onChange={(e) => setEditCVO({ ...editCVO, email: e.target.value })}
              placeholder="Enter email address"
              disabled={loading}
            />
          </FormField>

          <FormField label="Mobile Number" required>
            <Input
              type="tel"
              id="edit-cvo-mobile"
              value={editCVO?.mobile || ''}
              onChange={(e) => setEditCVO({ ...editCVO, mobile: e.target.value })}
              placeholder="Enter mobile number"
              disabled={loading}
              maxLength={10}
            />
          </FormField>

          <div className="md:col-span-2">
            <FormField label="Menu IDs" required hint="Select one or more menu IDs for this CVO">
              <MultiSelect
                id="edit-cvo-menu-ids"
                options={cvoPrivileges}
                value={editCVO?.menu_ids || []}
                onChange={(values) => setEditCVO({ ...editCVO, menu_ids: values })}
                placeholder="Select menu IDs..."
                disabled={loading}
              />
            </FormField>
          </div>
        </div>
      </BaseDialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        message={`Are you sure you want to delete CVO "${cvoToDelete?.name}"?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setCvoToDelete(null);
        }}
        loading={loading}
      />

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className={`rounded-lg shadow-xl p-4 flex items-center space-x-3 ${notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
            } text-white`}>
            <div>
              {notification.type === 'success' ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification({ show: false, message: '', type: 'success' })}
              className="text-white hover:text-gray-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AddCVOView;

